import { z } from 'zod';
import {
    IdentityProviderType,
    SsoConnectionAddParametersDomain,
    SsoOpenIdConnectionResult,
    SsoOpenIdConnectionResultUserNotification,
    useRepositoryMutation,
} from '@mews-commander/data-access';
import {
    createExperimentalZodFormResolver,
    lt,
    showErrorAlert,
    showSuccessAlert,
    useFormControls,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';

export enum ConfigureSsoPanelFieldNames {
    IdentityProvider = 'identityProviderType',
    IdpDomain = 'idpDomain',
    ClientId = 'clientId',
    ClientSecret = 'clientSecret',
    ExpirationDate = 'expirationDate',
    Domains = 'domains',
    EnforcedDomains = 'enforcedDomains',
    UserNotifications = 'userNotifications',
}

export interface ConfigureSsoPanelFormData {
    identityProviderType: IdentityProviderType;
    idpDomain: string;
    clientId: string;
    clientSecret: string;
    expirationDate: string;
    domains: string[];
    enforcedDomains: Record<string, boolean>;
    userNotifications: string[];
}

const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]{1,63}\.)+[a-zA-Z]{2,}$/;

export const ssoIdentityValidationSchema = {
    [ConfigureSsoPanelFieldNames.IdentityProvider]: z.string().min(1),
    [ConfigureSsoPanelFieldNames.IdpDomain]: z
        .string()
        .regex(domainRegex, {
            message: TextKey.InvalidUrlErrorMessage,
        })
        .min(1),
    [ConfigureSsoPanelFieldNames.ClientId]: z.string().min(1),
};

export const ssoSecretValidationSchema = {
    [ConfigureSsoPanelFieldNames.ClientSecret]: z.string().min(1),
    [ConfigureSsoPanelFieldNames.ExpirationDate]: z.string().refine(isFutureDate, {
        message: TextKey.ValidateFutureDateErrorMessage,
    }),
    [ConfigureSsoPanelFieldNames.UserNotifications]: z.array(z.string()),
};

function isFutureDate(date: string) {
    return new Date(date) > new Date();
}

export const ssoDomainsSchema = {
    [ConfigureSsoPanelFieldNames.Domains]: z.array(z.string()).min(1),
    [ConfigureSsoPanelFieldNames.EnforcedDomains]: z.record(z.boolean()),
};

const newSsoConnectionSchema = z.object({
    ...ssoIdentityValidationSchema,
    ...ssoSecretValidationSchema,
    ...ssoDomainsSchema,
});

export const initialSsoValues = {
    [ConfigureSsoPanelFieldNames.IdentityProvider]: undefined,
    [ConfigureSsoPanelFieldNames.IdpDomain]: '',
    [ConfigureSsoPanelFieldNames.ClientId]: '',
    [ConfigureSsoPanelFieldNames.ClientSecret]: '',
    [ConfigureSsoPanelFieldNames.ExpirationDate]: '',
    [ConfigureSsoPanelFieldNames.Domains]: [],
    [ConfigureSsoPanelFieldNames.EnforcedDomains]: {},
    [ConfigureSsoPanelFieldNames.UserNotifications]: [],
};

interface UseConfigureSsoPanelFormProps {
    id: string;
    onError?: (error: Error) => void;
    onSuccess: () => void;
}

export function useConfigureSsoPanelForm({ id, onError, onSuccess }: UseConfigureSsoPanelFormProps) {
    const { mutateAsync } = useRepositoryMutation('ssoConnection', 'addOpenId', {
        onSuccess: () => {
            showSuccessAlert({
                title: lt('SsoActivatedAlertTitle'),
                description: lt('SsoActivatedAlertDescription'),
            });
            onSuccess();
        },
        invalidateQueries: {
            ssoConnection: ['get'],
        },
        meta: {
            disableDefaultErrorHandler: true,
        },
    });

    const { validate, errors, ...otherMethods } = useFormControls<ConfigureSsoPanelFormData>({
        id,
        onSubmit: async (values: ConfigureSsoPanelFormData) => {
            try {
                await mutateAsync(parseFormToSsoConfig(values));
            } catch (error) {
                showErrorAlert({ description: <>{error.message}</> });
                onError?.(error);
            }
        },
        mode: 'onBlur',
        defaultValues: initialSsoValues,
        resolver: createExperimentalZodFormResolver(newSsoConnectionSchema),
    });

    const validateConfig = async () =>
        validate([
            ConfigureSsoPanelFieldNames.IdentityProvider,
            ConfigureSsoPanelFieldNames.IdpDomain,
            ConfigureSsoPanelFieldNames.ClientId,
            ConfigureSsoPanelFieldNames.ClientSecret,
            ConfigureSsoPanelFieldNames.ExpirationDate,
            ConfigureSsoPanelFieldNames.UserNotifications,
        ]);

    const hasValidConnectionConfig =
        !errors.identityProviderType &&
        !errors.idpDomain &&
        !errors.clientId &&
        !errors.expirationDate &&
        !errors.clientSecret;

    return {
        validate,
        validateConfig,
        hasValidConnectionConfig,
        ...otherMethods,
    };
}

export function parseFormToSsoConfig({ domains, enforcedDomains, ...ssoConfig }: ConfigureSsoPanelFormData) {
    return {
        ...ssoConfig,
        userNotifications: ssoConfig.userNotifications.map(parseFormDataToUserNotification),
        domains: parseFormUserDomains({ domains, enforcedDomains }),
    };
}

export function parseFormUserDomains({
    domains,
    enforcedDomains,
}: Pick<ConfigureSsoPanelFormData, 'domains' | 'enforcedDomains'>): SsoConnectionAddParametersDomain[] {
    return domains.map((domain) => ({
        domain,
        enforceSingleSignOn: !!enforcedDomains[domain],
    }));
}

export function parseSsoConfigToForm(data: SsoOpenIdConnectionResult): ConfigureSsoPanelFormData {
    return {
        clientId: data.clientId || '',
        clientSecret: '',
        idpDomain: data.idpDomain || '',
        identityProviderType: data.identityProviderType || IdentityProviderType.MicrosoftAzure,
        domains: (data.domains || []).filter((domain) => !!domain).map((domain) => domain.domain as string),
        expirationDate: data.expirationDate || '',
        enforcedDomains: (data.domains || []).reduce<Record<string, boolean>>((acc, domain) => {
            acc[domain.domain as string] = !!domain.enforceSingleSignOn;
            return acc;
        }, {}),
        userNotifications: (data.userNotifications || []).map(parseUserNotificationToFormData),
    };
}

/*
 * We transform the value to a string to make more manageable in the form state.
 */

export function parseUserNotificationToFormData({
    userId,
    userEmailAddress,
}: SsoOpenIdConnectionResultUserNotification) {
    return JSON.stringify({ userId, userEmailAddress });
}

export function parseFormDataToUserNotification(userNotificationFormData: string) {
    const { userId, userEmailAddress } = JSON.parse(userNotificationFormData) as {
        userId: string;
        userEmailAddress: string;
    };
    return { userId, userEmailAddress };
}
